import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus, Package, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn, formatKD } from "@/lib/utils";
import { type Product } from "@/lib/types";
import { useShopCart } from "@/lib/cart";
import { useProductCategories, useProducts } from "@/hooks/use-products";

/**
 * The shop, inside the booking page. Customers add products to the shared
 * shop cart without navigating away, so the booking they are filling in
 * (date, slot, services, address, coupon) stays exactly as it is.
 */
export function BookingShopSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { data: products, isLoading } = useProducts();
  const { data: categories } = useProductCategories();
  const cart = useShopCart();
  const [categoryFilter, setCategoryFilter] = useState("all");
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const list = products || [];
    return categoryFilter === "all" ? list : list.filter((p) => p.categoryId === categoryFilter);
  }, [products, categoryFilter]);

  // "All" + every category that actually has products, each with its count.
  const chips = useMemo(() => {
    const list = products || [];
    const countFor = (id: string) => list.filter((p) => p.categoryId === id).length;
    return [{ id: "all", name: t("shop.allProducts"), count: list.length }].concat(
      (categories || [])
        .map((c) => ({ id: c.id, name: isRtl ? c.nameAr : c.nameEn, count: countFor(c.id) }))
        .filter((c) => c.count > 0),
    );
  }, [products, categories, isRtl, t]);

  const selectCategory = (id: string) => {
    setCategoryFilter(id);
    listRef.current?.scrollTo({ top: 0 });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        dir={isRtl ? "rtl" : "ltr"}
        className="max-h-[88dvh] flex flex-col gap-0 rounded-t-3xl p-0 sm:max-w-2xl sm:mx-auto"
        data-testid="booking-shop-sheet"
      >
        <SheetHeader className="shrink-0 px-5 pr-12 pt-5 pb-3 text-start">
          <SheetTitle>{t("shop.sheet.title")}</SheetTitle>
          <SheetDescription>{t("shop.sheet.subtitle")}</SheetDescription>
        </SheetHeader>

        {/* shrink-0: a horizontal scroller has no min height, so in the
            height-capped column it would otherwise be squashed flat. */}
        <div
          role="tablist"
          className="flex shrink-0 gap-2 overflow-x-auto px-5 pt-1 pb-3 scrollbar-hide"
        >
          {chips.map((option) => {
            const active = categoryFilter === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => selectCategory(option.id)}
                className={cn(
                  "inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition-colors",
                  active
                    ? "border-primary bg-primary text-primary-foreground shadow-sm"
                    : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground",
                )}
                data-testid={`booking-shop-filter-${option.id}`}
              >
                {option.name}
                <span
                  className={cn(
                    "min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 tabular-nums",
                    active ? "bg-primary-foreground/20" : "bg-muted",
                  )}
                >
                  {option.count}
                </span>
              </button>
            );
          })}
        </div>

        <ul ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 space-y-2">
          {isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />
            ))}
          {!isLoading && filtered.length === 0 && (
            <li className="py-10 text-center text-sm text-muted-foreground">{t("shop.empty")}</li>
          )}
          {filtered.map((product) => (
            <ProductRow
              key={product.id}
              product={product}
              isRtl={isRtl}
              quantity={cart.items.find((i) => i.productId === product.id)?.quantity ?? 0}
              onAdd={() => cart.addItem(product, 1)}
              onRemove={() => cart.decrementItem(product.id)}
            />
          ))}
        </ul>

        <div className="shrink-0 border-t border-border px-5 py-4">
          <Button
            type="button"
            className="w-full rounded-full"
            onClick={() => onOpenChange(false)}
            data-testid="booking-shop-done"
          >
            {cart.count > 0
              ? t("shop.sheet.doneWithTotal", {
                  count: cart.count,
                  total: `${cart.subtotal.toFixed(3)} ${t("services.currency")}`,
                })
              : t("shop.sheet.done")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ProductRow({
  product,
  isRtl,
  quantity,
  onAdd,
  onRemove,
}: {
  product: Product;
  isRtl: boolean;
  quantity: number;
  onAdd: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const name = isRtl ? product.nameAr : product.nameEn;
  const lowStock = product.inStock && product.stockQuantity <= 5;

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-2xl border p-2.5 transition-colors",
        quantity > 0 ? "border-primary/60 bg-primary/5" : "border-border bg-card",
      )}
      data-testid={`booking-shop-product-${product.slug}`}
    >
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl bg-muted">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package className="h-6 w-6 text-muted-foreground/40" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{name}</p>
        <p className="text-xs text-muted-foreground">
          {[product.brand, product.size].filter(Boolean).join(" · ")}
        </p>
        <p className="mt-0.5 text-sm">
          <span className="font-bold text-primary">{formatKD(product.price)}</span>{" "}
          <span className="text-[10px] text-muted-foreground">{t("services.currency")}</span>
          {!product.inStock && (
            <span className="ms-2 text-xs text-destructive">{t("shop.outOfStock")}</span>
          )}
          {lowStock && (
            <span className="ms-2 text-xs text-muted-foreground">
              {t("shop.lowStock", { count: product.stockQuantity })}
            </span>
          )}
        </p>
      </div>

      {product.inStock &&
        (quantity === 0 ? (
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-9 w-9 flex-shrink-0 rounded-full border-primary/40 text-primary"
            aria-label={t("shop.addToCart")}
            onClick={onAdd}
            data-testid={`booking-shop-add-${product.slug}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        ) : (
          <span className="inline-flex flex-shrink-0 items-center rounded-full bg-primary text-primary-foreground">
            <button
              type="button"
              aria-label="-"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-primary-foreground/15"
              onClick={onRemove}
              data-testid={`booking-shop-minus-${product.slug}`}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="w-5 text-center text-xs font-bold tabular-nums">{quantity}</span>
            <button
              type="button"
              aria-label="+"
              className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-primary-foreground/15 disabled:opacity-40"
              disabled={quantity >= product.stockQuantity}
              onClick={onAdd}
              data-testid={`booking-shop-plus-${product.slug}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
    </li>
  );
}
